import { Link } from "react-router-dom";
import SuccessSvg from "@/components/shared/svgs/success-svg";
import ErrorSvg from "@/components/shared/svgs/error-svg";
import { buttonVariants } from "@/components/button";
import CircularLoading from "@/components/circular-loading";
import { fa } from "@/features/course/components/course-format";
import { formatJalaliDateTime } from "@/features/quiz/utils/format";
import {
  CertificateOrderKind,
  type CertificateNextAction,
  type CertificatePaymentPayload,
} from "@/models/certificate/PlatformCertificate";
import {
  findCertificateEntry,
  platformCertificateHref,
  usePlatformCertificateStatus,
} from "../hooks/use-platform-certificate";

const NEXT_ACTION_LABELS: Partial<Record<CertificateNextAction, string>> = {
  start_exam: "شروع آزمون",
  resume_exam: "ادامه آزمون",
  pay_retry: "خرید تلاش مجدد",
  pay_issuance: "ثبت سفارش گواهی",
  awaiting_review: "مشاهده وضعیت گواهی",
  awaiting_issue: "مشاهده وضعیت گواهی",
  view_certificate: "مشاهده گواهی",
  rejected: "مشاهده وضعیت گواهی",
};

/** Fallback for a payload that predates `order_kind_label`. */
const ORDER_KIND_LABELS: Record<CertificateOrderKind, string> = {
  [CertificateOrderKind.Retry]: "تلاش مجدد آزمون",
  [CertificateOrderKind.Issuance]: "صدور گواهی",
  [CertificateOrderKind.ResendShipping]: "ارسال مجدد گواهی",
};

/** Neutral mark for a payment whose outcome the backend has not settled yet. */
function PendingMark() {
  return (
    <span className="flex size-16 items-center justify-center rounded-full bg-warning/10">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="text-warning" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 7.5V12l2.75 1.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function InfoRow({ label, value, ltr }: { label: string; value?: string; ltr?: boolean }) {
  return (
    <div className="flex w-full items-center gap-2">
      <span className="shrink-0 text-default-500">{label}</span>
      <span className="flex-1 border-b border-dotted border-default-300" />
      <span className="shrink-0 font-semibold text-default-800" dir={ltr ? "ltr" : undefined}>
        {value || "-"}
      </span>
    </div>
  );
}

/**
 * The certificate branch of the shared payment result page. The payload is only
 * a display hint — the button comes from a fresh status fetch, which the status
 * hook guarantees (it never persists and always refetches on mount).
 */
export default function CertificatePaymentResult({
  payload,
}: {
  payload: CertificatePaymentPayload;
}) {
  const miniAppUuid = payload.mini_app_uuid ?? "";
  const courseId = payload.course_id ?? "";
  const statusQuery = usePlatformCertificateStatus({ miniAppUuid, courseId });

  const paid = payload.status === "Completed";
  // Anything that is neither of the two documented statuses (e.g. a SnappPay
  // "Pending") must not be rendered as a failure — the money may well be gone.
  const undecided = !paid && payload.status !== "Failed";
  const productLabel = [
    payload.order_kind_label || ORDER_KIND_LABELS[payload.order_kind],
    payload.offering_title ? `«${payload.offering_title}»` : null,
  ]
    .filter(Boolean)
    .join(" ");
  const canReturn = Boolean(miniAppUuid && courseId);

  // A course may sell two certificates; the payload says which one this paid
  // for, and the buyer goes back to that one's page. Without it (a server
  // older than guide v8) the course page is the only honest destination —
  // every certificate has its own card there.
  const offeringId = payload.offering_id;
  const entry = offeringId != null ? findCertificateEntry(statusQuery.data, offeringId) : undefined;
  const returnHref =
    offeringId != null
      ? platformCertificateHref(courseId, miniAppUuid, offeringId)
      : `/course/${courseId}?miniAppUuid=${encodeURIComponent(miniAppUuid)}`;
  const returnLabel =
    offeringId == null
      ? "بازگشت به دوره"
      : ((entry && NEXT_ACTION_LABELS[entry.next_action]) ?? "بازگشت به گواهی");

  return (
    <div className="w-full max-w-md overflow-hidden rounded-2xl border border-default-100 bg-background shadow-lg shadow-default-900/5">
      <div className="flex flex-col items-center gap-3 px-4 pb-4 pt-6">
        {paid ? <SuccessSvg /> : undecided ? <PendingMark /> : <ErrorSvg />}
        <p className="text-base font-bold text-default-900">
          {paid ? "پرداخت موفق" : undecided ? "پرداخت در حال بررسی" : "پرداخت ناموفق"}
        </p>
        {undecided ? (
          <p className="text-center text-xs leading-6 text-default-500">
            نتیجهٔ نهایی هنوز اعلام نشده است. وضعیت گواهی پس از تأیید بانک به‌روز می‌شود.
          </p>
        ) : null}
        <p className="text-center text-sm leading-7 text-default-600">
          {productLabel}
          {payload.course_name ? (
            <>
              {" — دوره "}
              <span className="font-bold text-default-900">{payload.course_name}</span>
            </>
          ) : null}
        </p>
        {!paid && !undecided && payload.reason ? (
          <p className="text-center text-xs leading-6 text-danger">{payload.reason}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-4 px-5 pb-5">
        <div className="flex flex-col gap-5 rounded-xl bg-content3/40 p-4 text-xs">
          {payload.price != null ? (
            <InfoRow label="مبلغ پرداختی" value={`${fa(payload.price)} تومان`} />
          ) : null}
          <InfoRow
            label="کد سفارش"
            value={payload.order_reference || payload.factor_id}
            ltr
          />
          <InfoRow
            label="تاریخ"
            value={formatJalaliDateTime(payload.paid_at ?? payload.created_at)}
          />
        </div>

        {!canReturn ? (
          <Link to="/my-courses" replace className={buttonVariants({ className: "w-full" })}>
            دوره‌های من
          </Link>
        ) : statusQuery.isPending ? (
          null
        ) : (
          <Link to={returnHref} replace className={buttonVariants({ className: "w-full" })}>
            {returnLabel}
          </Link>
        )}
      </div>
    </div>
  );
}
